import { useListMatchFailures, useResolveMatchFailure } from "@workspace/api-client-react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { formatCurrency, safeDateFormat } from "@/lib/utils";
import { AlertTriangle, CheckCircle, XCircle, ArrowRight } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

export default function MatchFailures() {
  const { data: failures, isLoading } = useListMatchFailures({ reviewed: false });
  const resolveMutation = useResolveMatchFailure();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const handleResolve = (id: number, action: 'approve' | 'reject') => {
    resolveMutation.mutate({ id, data: { action } }, {
      onSuccess: () => {
        toast({ title: `Match ${action}d successfully` });
        queryClient.invalidateQueries({ queryKey: ["/api/match-failures"] });
        queryClient.invalidateQueries({ queryKey: ["/api/statements"] });
      }
    });
  };

  return (
    <Layout>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold text-slate-900">Review Queue</h1>
          <p className="text-slate-500 mt-1">Manual review needed for ambiguous matches.</p>
        </div>
        <div className="bg-amber-100 text-amber-800 px-4 py-2 rounded-xl font-medium text-sm flex items-center">
          <AlertTriangle className="w-4 h-4 mr-2" /> {failures?.length || 0} items to review
        </div>
      </div>

      <div className="space-y-6">
        {isLoading && <div className="text-center py-12 text-slate-500">Loading review queue...</div>}
        
        {failures?.map((failure) => (
          <Card key={failure.id} className="overflow-hidden border-amber-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="bg-amber-50 px-6 py-3 border-b border-amber-100 flex items-center justify-between">
              <div className="flex items-center gap-2 text-amber-800 font-medium text-sm">
                <AlertTriangle className="w-4 h-4" /> Reason: {failure.reason}
              </div>
              <span className="text-xs text-amber-600">Created {safeDateFormat(failure.createdAt)}</span>
            </div>
            <CardContent className="p-0">
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] divide-y lg:divide-y-0 lg:divide-x divide-slate-200">
                
                {/* Receipt Side */}
                <div className="p-6 bg-white">
                  <p className="text-xs font-bold tracking-wider text-slate-400 uppercase mb-4">Uploaded Receipt</p>
                  {failure.receipt ? (
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-slate-500">Vendor</p>
                        <p className="font-medium text-slate-900 text-lg">{failure.receipt.vendor || 'Unknown'}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-slate-500">Date</p>
                          <p className="font-medium text-slate-900">
                            {safeDateFormat(failure.receipt.receiptDate)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Amount</p>
                          <p className="font-bold text-slate-900 text-xl text-primary">
                            {formatCurrency(failure.receipt.amount || 0, failure.receipt.currency || 'GBP')}
                          </p>
                        </div>
                      </div>
                      <a href={`/api/receipts/${failure.receipt.id}/download`} target="_blank" className="text-sm text-primary hover:underline inline-block mt-2">View original file</a>
                    </div>
                  ) : <p className="text-slate-500">Receipt data missing</p>}
                </div>

                {/* Arrow */}
                <div className="hidden lg:flex items-center justify-center p-6 bg-slate-50">
                  <ArrowRight className="w-6 h-6 text-slate-400" />
                </div>

                {/* Statement Entry Side */}
                <div className="p-6 bg-white">
                  <p className="text-xs font-bold tracking-wider text-slate-400 uppercase mb-4">Statement Entry</p>
                  {failure.entry ? (
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-slate-500">Cardholder</p>
                        <p className="font-medium text-slate-900 text-lg">{failure.entry.cardholderName}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-slate-500">Date</p>
                          <p className="font-medium text-slate-900">
                            {safeDateFormat(failure.entry.transactionDate)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Amount</p>
                          <p className="font-bold text-slate-900 text-xl text-primary">
                            {formatCurrency(failure.entry.amount)}
                          </p>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Description</p>
                        <p className="text-sm font-medium text-slate-900 truncate" title={failure.entry.description}>{failure.entry.description}</p>
                      </div>
                    </div>
                  ) : <p className="text-slate-500">Entry data missing</p>}
                </div>

              </div>

              <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-3">
                <Button 
                  variant="outline" 
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  onClick={() => handleResolve(failure.id, 'reject')}
                  disabled={resolveMutation.isPending}
                >
                  <XCircle className="w-4 h-4 mr-2" /> Reject Match
                </Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => handleResolve(failure.id, 'approve')}
                  disabled={resolveMutation.isPending}
                >
                  <CheckCircle className="w-4 h-4 mr-2" /> Force Approve
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {!isLoading && failures?.length === 0 && (
          <div className="text-center py-24 bg-white rounded-2xl border border-dashed border-slate-300">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-1">All Caught Up!</h3>
            <p className="text-slate-500">There are no failed matches requiring your review.</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
